<script setup lang="ts">
/**
 * ReadAloud — B1 da Proposta Lume, Fase 2.
 *
 * HLOS-323: 2 caminhos de leitura (TTS + texto grande modal).
 * TTS usa AppleTTSBridge no iOS (AVSpeechSynthesizer) ou
 * Web Speech API em outras plataformas.
 *
 * Constitucional:
 * - P-UX-10 (Quiet as feature): só fala sob clique.
 * - P-UX-08 (Dignified feedback): erros visíveis e honestos.
 * - P-UX-05 (Sovereignty tangible): alternativa visual sempre presente.
 */
import { ref, computed } from "vue";
import { useAccessibility } from "../../accessibility/composables/useAccessibility";
import { useVoiceOutput } from "../../accessibility/composables/useVoiceOutput";

interface Props {
  text: string;
  lang?: string;
}

const props = withDefaults(defineProps<Props>(), { lang: "pt-BR" });
const a11y = useAccessibility();
const voice = useVoiceOutput();

const showLargeText = ref(false);

function onTtsClick() {
  if (voice.isSpeaking.value) {
    voice.cancel();
  } else {
    voice.speak(props.text, {
      lang: props.lang,
      rate: a11y.preferences.value.voiceRate,
      pitch: a11y.preferences.value.voicePitch,
      volume: 1.0,
    });
  }
}

function openLargeText() {
  showLargeText.value = true;
}

function closeLargeText() {
  showLargeText.value = false;
}

const errorMessage = (code: string | null): string | null => {
  if (!code) return null;
  switch (code) {
    case "unsupported":
      return "Síntese de voz não suportada. Use o modo texto grande abaixo.";
    case "no-voice":
      return "Nenhuma voz pt-BR. Use o modo texto grande abaixo.";
    case "synthesis-failed":
    case "synthesis-unavailable":
      return "Falha na síntese. Use o modo texto grande abaixo.";
    case "audio-busy":
      return "Áudio ocupado. Pare outra leitura antes.";
    case "audio-hardware":
      return "Hardware de áudio indisponível. Use o modo texto grande abaixo.";
    case "network":
      return "Síntese precisa de conexão. Use o modo texto grande abaixo.";
    default:
      return `Erro de síntese: ${code}. Use o modo texto grande abaixo.`;
  }
};

const largeTextFontSize = computed(() => {
  const s = a11y.preferences.value.fontSize;
  if (s === "x-large") return "1.5rem";
  if (s === "large") return "1.25rem";
  return "1.0625rem";
});
</script>

<template>
  <div class="hlos-read-aloud">
    <div
      v-if="a11y.preferences.value.voiceOutputEnabled"
      class="hlos-read-aloud__toolbar"
    >
      <button
        type="button"
        class="hlos-read-aloud__btn hlos-read-aloud__btn--tts"
        :class="{ 'hlos-read-aloud__btn--active': voice.isSpeaking.value }"
        :aria-pressed="voice.isSpeaking.value"
        :aria-label="
          voice.isSpeaking.value
            ? 'Parar leitura em voz alta'
            : 'Ouvir mensagem em voz alta'
        "
        :title="voice.voicesReady.value ? '' : 'Carregando vozes...'"
        data-testid="read-aloud"
        @click="onTtsClick"
      >
        <span aria-hidden="true">{{ voice.isSpeaking.value ? "■" : "🔊" }}</span>
        <span class="hlos-read-aloud__btn-label">
          {{ voice.isSpeaking.value ? "Parar" : "Ouvir" }}
        </span>
      </button>
      <button
        type="button"
        class="hlos-read-aloud__btn hlos-read-aloud__btn--text"
        aria-label="Ver texto em tamanho grande"
        title="Ver texto em tamanho grande (alternativa ao áudio)"
        data-testid="read-aloud-large"
        @click="openLargeText"
      >
        <span aria-hidden="true">Aa+</span>
        <span class="hlos-read-aloud__btn-label">Texto grande</span>
      </button>
    </div>

    <p
      v-if="errorMessage(voice.error.value)"
      class="hlos-read-aloud__error"
      role="alert"
      data-testid="read-aloud-error"
    >
      {{ errorMessage(voice.error.value) }}
    </p>

    <!-- HLOS-322: modal de texto grande como alternativa honesta -->
    <Teleport to="body">
      <Transition name="hlos-fade">
        <div
          v-if="showLargeText"
          class="hlos-read-aloud-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="hlos-read-aloud-modal-title"
        >
          <div class="hlos-read-aloud-modal__backdrop" @click="closeLargeText" />
          <div class="hlos-read-aloud-modal__panel">
            <header class="hlos-read-aloud-modal__header">
              <h2 id="hlos-read-aloud-modal-title">Texto grande</h2>
              <button
                type="button"
                class="hlos-read-aloud-modal__close"
                aria-label="Fechar"
                @click="closeLargeText"
              >
                Fechar
              </button>
            </header>
            <p
              class="hlos-read-aloud-modal__text"
              :style="{ fontSize: largeTextFontSize, lineHeight: 1.7 }"
            >
              {{ text }}
            </p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.hlos-read-aloud {
  display: inline-flex;
  flex-direction: column;
  gap: 0.375rem;
}
.hlos-read-aloud__toolbar {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: stretch;
}
.hlos-read-aloud__btn {
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.4rem 0.75rem;
  border: 1px solid var(--hlos-color-neutral-300);
  border-radius: 6px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 0.85em;
  line-height: 1.2;
  white-space: nowrap;
  min-height: 32px;
}
.hlos-read-aloud__btn:hover {
  background: var(--hlos-color-hover);
}
.hlos-read-aloud__btn--active {
  background: var(--hlos-color-primary);
  color: var(--hlos-color-bg);
  border-color: var(--hlos-color-primary);
}
.hlos-read-aloud__btn--tts {
  /* cor primária sutil para destacar TTS */
  border-color: var(--hlos-color-primary);
}
.hlos-read-aloud__btn--text {
  /* cor neutra para alternativa visual */
  opacity: 0.85;
}
.hlos-read-aloud__btn-label {
  /* impede quebra de linha feia em telas estreitas */
  white-space: nowrap;
}
.hlos-read-aloud__error {
  margin: 0;
  font-size: 0.75em;
  color: var(--hlos-color-error);
  max-width: 280px;
  line-height: 1.4;
}
.hlos-read-aloud-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.hlos-read-aloud-modal__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
}
.hlos-read-aloud-modal__panel {
  position: relative;
  background: var(--hlos-color-bg);
  color: var(--hlos-color-text);
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}
.hlos-read-aloud-modal__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.hlos-read-aloud-modal__header h2 {
  margin: 0;
  font-size: 1.25rem;
}
.hlos-read-aloud-modal__close {
  font: inherit;
  padding: 0.4rem 0.8rem;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}
.hlos-read-aloud-modal__text {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.hlos-fade-enter-active,
.hlos-fade-leave-active {
  transition: opacity 0.2s;
}
.hlos-fade-enter-from,
.hlos-fade-leave-to {
  opacity: 0;
}
</style>
